import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  Users,
  CheckCircle,
  Clock,
  MessageSquare,
  Activity,
  UploadCloud,
  Smile,
  Frown,
  Meh,
} from "lucide-react";
import { dashboardAPI, pdfAPI } from "../services/api";

interface OverviewResponse {
  metrics: {
    checkedInGuests: number;
    arrivalsToday: number;
    departuresToday: number;
    openTasks: number;
    completedToday: number;
  };
  tasks: {
    open: {
      id: string;
      guest_name: string;
      room_number: string;
      task_description: string;
      status: string;
      assigned_to?: string;
      created_at?: string;
    }[];
  };
  chats: {
    unread: number;
    escalated: number;
    aiPaused: number;
    recent: any[];
  };
  content: {
    categories: { name: string; status: string }[];
  };
  onboarding: { key: string; title: string; status: string }[];
  feedback: {
    last7d: { positive: number; neutral: number; negative: number; total: number; score: number };
    last30d: { positive: number; neutral: number; negative: number; total: number; score: number };
    recent: { guestName: string; room: string; rating: string; comment: string; timestamp: string }[];
  };
  activity: { type: string; message: string; timestamp: string }[];
}

const Dashboard: React.FC = () => {
  const [overview, setOverview] = useState<OverviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState<string | null>(null);
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  useEffect(() => {
    fetchOverview();
  }, []);

  const fetchOverview = async () => {
    try {
      const data = await dashboardAPI.getOverview();
      setOverview(data);
    } catch (error) {
      console.error("Error fetching dashboard overview:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadClick = (key: string) => {
    const ref = fileInputRefs.current[key];
    if (ref) {
      ref.value = "";
      ref.click();
    }
  };

  const handleFileChange = async (key: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploading(key);
      const category = key; // hint to backend; backend ignores but harmless
      await pdfAPI.uploadPdf(file, category);
      await fetchOverview();
    } catch (err) {
      console.error("Upload error:", err);
      alert("Upload failed. Please try again.");
    } finally {
      setUploading(null);
    }
  };

  const statCards = overview
    ? [
        {
          title: "Checked In",
          value: overview.metrics.checkedInGuests,
          icon: Users,
          color: "bg-green-500",
        },
        {
          title: "Arrivals Today",
          value: overview.metrics.arrivalsToday,
          icon: CheckCircle,
          color: "bg-blue-500",
        },
        {
          title: "Departures Today",
          value: overview.metrics.departuresToday,
          icon: Clock,
          color: "bg-amber-500",
        },
        {
          title: "Open Tasks",
          value: overview.metrics.openTasks,
          icon: CheckCircle,
          color: "bg-indigo-500",
        },
      ]
    : [];

  if (loading || !overview) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg text-grey-guestby">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-black-guestby">Dashboard</h1>
        <div className="flex items-center space-x-2 text-blue-guestby">
          <MessageSquare className="h-5 w-5" />
          <span className="text-sm font-medium">AI Concierge Active</span>
        </div>
      </div>

      {/* Onboarding + Health */}
      {/* Onboarding + key panels */}
      {overview.onboarding.some((s) => s.status !== "done") && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6 lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-black-guestby">
                Onboarding
              </h2>
            </div>
            <div className="space-y-3">
              {overview.onboarding.map((step) => (
                <div
                  key={step.key}
                  className="flex items-center justify-between p-3 rounded-lg border border-gray-100"
                >
                  <div>
                    <p className="text-sm font-semibold text-black-guestby">
                      {step.title}
                    </p>
                    <p className="text-xs text-grey-guestby">
                      Upload and process to power better answers.
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    {step.status !== "done" && (
                      <>
                        <button
                          onClick={() => handleUploadClick(step.key)}
                          className="text-xs bg-blue-guestby text-black-guestby px-3 py-1 rounded-md hover:bg-blue-400 transition-colors disabled:opacity-60"
                          disabled={uploading !== null}
                        >
                          {uploading === step.key ? "Uploading..." : "Upload"}
                        </button>
                        <input
                          type="file"
                          accept="application/pdf"
                          className="hidden"
                          ref={(el) => {
                            fileInputRefs.current[step.key] = el;
                          }}
                          onChange={(e) => handleFileChange(step.key, e)}
                        />
                      </>
                    )}
                    <span
                      className={`px-3 py-1 text-xs rounded-full ${
                        step.status === "done"
                          ? "bg-green-100 text-green-700"
                          : "bg-amber-100 text-amber-700"
                      }`}
                    >
                      {step.status === "done" ? "Done" : "Pending"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-black-guestby mb-4">
              Chats Needing Attention
            </h2>
            <div className="space-y-2">
              <p className="text-sm text-black-guestby">
                Unread: {overview.chats.unread} · Escalated:{" "}
                {overview.chats.escalated} · AI Paused: {overview.chats.aiPaused}
              </p>
              <p className="text-xs text-grey-guestby">
                Open the chat console to respond. AI stays silent when an agent is active.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((card, index) => (
          <div
            key={index}
            className="bg-white rounded-lg shadow-md p-6 border border-gray-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-grey-guestby">
                  {card.title}
                </p>
                <p className="text-3xl font-bold text-black-guestby mt-2">
                  {card.value}
                </p>
              </div>
              <div className={`p-3 rounded-full ${card.color}`}>
                <card.icon className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Tasks + Feedback */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-md border border-gray-200">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-xl font-semibold text-black-guestby">
              Tasks Needing Attention
            </h2>
            <UploadCloud className="h-5 w-5 text-blue-guestby" />
          </div>
          <div className="p-6 space-y-3">
            {overview.tasks.open.length === 0 && (
              <p className="text-sm text-grey-guestby">No open tasks.</p>
            )}
            {overview.tasks.open.map((task) => (
              <div
                key={task.id}
                className="flex items-center justify-between p-3 rounded-lg border border-gray-100"
              >
                <div>
                  <p className="text-sm font-medium text-black-guestby">
                    Room {task.room_number} — {task.guest_name || "Guest"}
                  </p>
                  <p className="text-xs text-grey-guestby">
                    {task.task_description}
                  </p>
                </div>
                <span
                  className={`px-3 py-1 text-xs rounded-full ${
                    task.status === "completed"
                      ? "bg-green-100 text-green-700"
                      : "bg-amber-100 text-amber-700"
                  }`}
                >
                  {task.status}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md border border-gray-200">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-xl font-semibold text-black-guestby">
              Guest Feedback
            </h2>
            <Smile className="h-5 w-5 text-blue-guestby" />
          </div>
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-green-50 text-green-800 rounded-lg p-3 text-center">
                <p className="text-xs uppercase font-semibold">Positive (7d)</p>
                <p className="text-xl font-bold">
                  {overview.feedback.last7d.positive}
                </p>
              </div>
              <div className="bg-amber-50 text-amber-800 rounded-lg p-3 text-center">
                <p className="text-xs uppercase font-semibold">Neutral (7d)</p>
                <p className="text-xl font-bold">
                  {overview.feedback.last7d.neutral}
                </p>
              </div>
              <div className="bg-red-50 text-red-800 rounded-lg p-3 text-center">
                <p className="text-xs uppercase font-semibold">Negative (7d)</p>
                <p className="text-xl font-bold">
                  {overview.feedback.last7d.negative}
                </p>
              </div>
            </div>
            <div className="space-y-3">
              {overview.feedback.recent.length === 0 && (
                <p className="text-sm text-grey-guestby">
                  No recent feedback yet.
                </p>
              )}
              {overview.feedback.recent.map((fb, idx) => (
                <div
                  key={idx}
                  className="p-3 rounded-lg border border-gray-100"
                >
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-sm font-semibold text-black-guestby">
                      {fb.guestName} • Room {fb.room}
                    </p>
                    <span
                      className={`px-2 py-1 text-xs rounded-full ${
                        fb.rating === "positive"
                          ? "bg-green-100 text-green-700"
                          : fb.rating === "neutral"
                          ? "bg-amber-100 text-amber-700"
                          : "bg-red-100 text-red-700"
                      }`}
                    >
                      {fb.rating}
                    </span>
                  </div>
                  <p className="text-sm text-black-guestby">{fb.comment}</p>
                  <p className="text-xs text-grey-guestby mt-1">
                    {new Date(fb.timestamp).toLocaleString()}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-black-guestby">
            Quick Actions
          </h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button className="flex items-center justify-center space-x-2 p-4 bg-blue-guestby text-black-guestby rounded-lg hover:bg-blue-400 transition-colors">
              <Users className="h-5 w-5" />
              <span className="font-medium">Add New Guest</span>
            </button>
            <button className="flex items-center justify-center space-x-2 p-4 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors">
              <CheckCircle className="h-5 w-5" />
              <span className="font-medium">Create Task</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
